import { useState, useCallback, useMemo, type CSSProperties } from "react";
import { useAuthApi } from "../api/useAuthApi";
import literals from "../assets/literals/english.json";
import { AuthUser, ValidationRules } from "../types/auth-types";
import { useNavigation } from "./useNavigation";
import { AppRoute } from "../types/routes";

const { validation } = literals;

type ToastState = {
  message: string;
  type: "success" | "error" | "info" | "warning";
  isVisible: boolean;
};

export const useLogin = (onAuthSuccess: (user: AuthUser) => void) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [forgotModalOpen, setForgotModalOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  const {
    handleAccountLogin,
    handleGoogleSignIn: handleGoogleSignInApi,
    handleForgotPassword: handleForgotPasswordApi,
  } = useAuthApi();
  const { replace } = useNavigation();

  const validationRules: ValidationRules = useMemo(
    () => ({
      email: {
        required: true,
        email: true,
        requiredMessage: validation.email_required_error,
        emailMessage: validation.valid_email_error,
      },
      password: {
        required: true,
        minLength: 3,
        requiredMessage: validation.password_required_error,
        minLengthMessage: validation.password_length_error,
        patternMessage: validation.password_pattern_error,
      },
    }),
    []
  );

  const showToast = useCallback((message: string, type: ToastState["type"]) => {
    setToast({ message, type, isVisible: true });
  }, []);

  const validate = useCallback(() => {
    const newErrors: { email?: string; password?: string } = {};
    let isValid = true;

    if (validationRules.email.required && !email) {
      newErrors.email = validationRules.email.requiredMessage;
      isValid = false;
    } else if (validationRules.email.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = validationRules.email.emailMessage;
      isValid = false;
    }

    if (validationRules.password.required && !password) {
      newErrors.password = validationRules.password.requiredMessage;
      isValid = false;
    } else if (validationRules.password.minLength && password.length < validationRules.password.minLength) {
      newErrors.password = validationRules.password.minLengthMessage;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  }, [email, password, validationRules]);

  const handleSignIn = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) return;

      setLoading(true);
      try {
        const response = await handleAccountLogin({ email, password });
        if (response) {
          showToast("Login successful", "success");
          onAuthSuccess({ id: response.user.id, email: response.user.email });
          return;
        }

        showToast("Login failed. Please try again.", "error");
      } catch (error: any) {
        const message =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Login failed. Please check your credentials.";
        showToast(message, "error");
      } finally {
        setLoading(false);
      }
    },
    [email, handleAccountLogin, onAuthSuccess, password, showToast, validate]
  );

  const handleGoogleSignIn = useCallback(async () => {
    setLoading(true);
    try {
      await handleGoogleSignInApi();
    } catch (error) {
      console.error("Google Sign In failed", error);
      showToast("Google sign in failed. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  }, [handleGoogleSignInApi, showToast]);

  const handleForgotPassword = useCallback(() => {
    setForgotEmail(email || "");
    setForgotModalOpen(true);
  }, [email]);

  const closeForgotModal = useCallback(() => {
    if (forgotSubmitting) return;
    setForgotModalOpen(false);
  }, [forgotSubmitting]);

  const submitForgotPassword = useCallback(async () => {
    const candidate = forgotEmail.trim();
    if (!candidate) {
      showToast("Please enter your email.", "error");
      return;
    }

    setForgotSubmitting(true);
    try {
      const result = await handleForgotPasswordApi(candidate);
      if (result.ok) {
        setForgotModalOpen(false);
        showToast(
          "Check your email for a password reset link.",
          "success"
        );
      } else {
        showToast(result.message || "Failed to start password reset.", "error");
      }
    } finally {
      setForgotSubmitting(false);
    }
  }, [forgotEmail, handleForgotPasswordApi, showToast]);

  const handleToastClose = useCallback(() => {
    setToast(null);
  }, []);

  const handleCreateAccount = useCallback(() => {
    replace(AppRoute.SIGNUP);
  }, [replace]);

  const handleEmailChange = useCallback((value: string) => {
    setEmail(value);
  }, []);

  const handlePasswordChange = useCallback((value: string) => {
    setPassword(value);
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const handleForgotEmailChange = useCallback((value: string) => {
    setForgotEmail(value);
  }, []);

  const signInButtonState = useMemo(
    () => ({
      disabled: loading,
      label: loading ? "Signing In..." : "Sign In",
      style: {
        opacity: loading ? 0.7 : 1,
        cursor: loading ? "not-allowed" : "pointer",
      } as CSSProperties,
    }),
    [loading]
  );

  return {
    email,
    password,
    showPassword,
    loading,
    errors,
    forgotModalOpen,
    forgotEmail,
    forgotSubmitting,
    toast,
    signInButtonState,
    handleSignIn,
    handleGoogleSignIn,
    handleForgotPassword,
    handleCreateAccount,
    closeForgotModal,
    submitForgotPassword,
    handleToastClose,
    handleEmailChange,
    handlePasswordChange,
    togglePasswordVisibility,
    handleForgotEmailChange,
  };
};
